import { useQuery } from '@tanstack/react-query';
import { http } from './http';

/**
 * Hooks React Query pour les 8 endpoints fusion V2 (cf. PR #48 backend).
 *
 * `staleTime` 30s par défaut depuis main.tsx — bien pour Mission Control
 * et Parent Dashboard où l'utilisateur navigue entre pages sans s'attendre
 * à du temps réel.
 */

// ─── Training (gaps #1 à #4) ────────────────────────────────────────────────

export interface RegularityResponse {
  days_window: number;
  days_active: number;
  rows: Array<{ day: string; attempts: number }>;
}
export function useRegularity(days = 90) {
  return useQuery<RegularityResponse>({
    queryKey: ['training', 'regularity', days],
    queryFn: () => http.get(`/api/v1/training/regularity?days=${days}`),
  });
}

export interface ModuleStats {
  mastery_pct: number;
  attempts: number;
  success: number;
  last_active: string | null;
}
export interface ModulesSummaryResponse {
  days_window: number;
  modules: {
    opening: ModuleStats;
    tactic: ModuleStats;
    endgame: ModuleStats;
    autopsy: ModuleStats;
  };
}
export function useModulesSummary(days = 30) {
  return useQuery<ModulesSummaryResponse>({
    queryKey: ['training', 'modules-summary', days],
    queryFn: () => http.get(`/api/v1/training/modules-summary?days=${days}`),
  });
}

export interface PhaseSummaryResponse {
  days_window: number;
  phases: {
    opening: number;
    middlegame: number;
    endgame: number;
    total: number;
  };
}
export function usePhaseSummary(days = 30) {
  return useQuery<PhaseSummaryResponse>({
    queryKey: ['training', 'phase-summary', days],
    queryFn: () => http.get(`/api/v1/training/phase-summary?days=${days}`),
  });
}

export interface SessionRow {
  id: string;
  date: string;
  start_time: string;
  duration_min: number;
  attempts: number;
  success: number;
  modules: string[];
  avg_sec: number;
}
export interface SessionsResponse {
  count: number;
  sessions: SessionRow[];
}
export function useRecentSessions(limit = 5) {
  return useQuery<SessionsResponse>({
    queryKey: ['training', 'sessions', limit],
    queryFn: () => http.get(`/api/v1/training/sessions?limit=${limit}`),
  });
}

// ─── Today (cards dues par module + targets/done MVP2.5 T6) ────────────────

/**
 * MVP2.6 Gap 4 (P2.1) — Mission Control adaptatif V1.
 *
 * Chaque tâche priorisée par `AdaptiveBriefingComposer` côté backend
 * (cf. api/src/Services/AdaptiveBriefingComposer.php). Le frontend décide
 * d'opt-in adaptatif (utiliser `adaptive_brief`) ou de retomber sur les
 * compteurs statiques `<module>_target_today`.
 */
export interface AdaptiveBriefTask {
  key: string;
  label: string;
  module: 'opening' | 'tactic' | 'endgame' | 'autopsy';
  /** Raison de la priorisation : time_pressure_gap | regressing_skill | recurring_mistake | fallback. */
  priority_reason:
    | 'time_pressure_gap'
    | 'regressing_skill'
    | 'recurring_mistake'
    | 'fallback';
  /** Mode d'exécution (ex: tactic_drill_time_pressed pour time_pressure_gap). */
  mode?: string;
  skill_id?: string;
  pattern_tag?: string;
}

export interface TodayResponse {
  cards_due_count: number;
  opening_due_count?: number;
  tactics_due_count: number;
  endgames_due_count: number;
  // MVP2.5 T6 — rattachement BriefTask aux compteurs "du jour".
  // Champs optionnels pour rester compat avec une prod qui aurait servi
  // l'ancienne version pendant le rollout.
  opening_target_today?: number;
  tactics_target_today?: number;
  endgames_target_today?: number;
  autopsy_target_today?: number;
  opening_done_today?: number;
  tactics_done_today?: number;
  endgame_done_today?: number;
  autopsy_done_today?: number;
  // P1 Practice Arena T6 — Arène du jour
  practice_target_today?: number;
  practice_done_today?: number;
  // MVP2.6 Gap 3 (P2.6) — KPIs Mission Control élargis.
  streak_days?: number;
  recall_opening_pct?: number | null;
  blunder_rate_7d?: number;
  // MVP2.6 Gap 4 (P2.1) — Mission Control adaptatif.
  adaptive_brief?: AdaptiveBriefTask[];
}
export function useTodayCards() {
  return useQuery<TodayResponse>({
    queryKey: ['training', 'today'],
    queryFn: () => http.get('/api/v1/training/today'),
  });
}

// ─── Users (gaps #5 + #8) ──────────────────────────────────────────────────

export interface EloPoint {
  v: number;
  label: string;
  recorded_at: string;
}
export interface EloHistoryResponse {
  series: Record<string, EloPoint[]>;
  kinds: string[];
}
export function useEloHistory() {
  return useQuery<EloHistoryResponse>({
    queryKey: ['users', 'elo-history'],
    queryFn: () => http.get('/api/v1/users/me/elo-history'),
  });
}

// T4 P1 Stats Hub : `useChesscomRatings()` + `ChesscomRatingsResponse`
// supprimés — fusion dans `useRatings()` (cf. RATINGS_AUDIT §4.2 + api/ratings.ts).
// L'endpoint backend `GET /users/me/chesscom-ratings` reste actif (consommé
// par les crons sync), seul le hook frontend est retiré.

// ─── Parent Dashboard (gap #7) ─────────────────────────────────────────────

/**
 * T9.4 — trend par compétence sur dashboard parent.
 * Calculé comparatif fenêtre courante vs précédente côté backend.
 *
 *  - `improving`         : fails courante ≤ 80% précédente
 *  - `declining`         : fails courante ≥ 120% précédente (ou prev=0)
 *  - `stable`            : entre les deux
 *  - `insufficient_data` : < 5 fails total (pas de signal sur du bruit)
 */
export type TrendKind = 'improving' | 'stable' | 'declining' | 'insufficient_data';

/**
 * T9.2 — KPI central de la couche learning (intelligence-layer-vision §9).
 */
export interface RecurringMistakeRate {
  rate: number;
  recurring: number;
  total: number;
  top_recurring: Array<{ tag: string; count: number }>;
}

export interface ParentDashboardResponse {
  period_days: number;
  regularity: Array<{ day: string; attempts: number }>;
  regularity_30d: Array<{ day: string; attempts: number }>;
  cards_backlog: number;
  cards_due_today: number;
  weaknesses_top5: Array<{ theme: string; fails: number; trend?: TrendKind }>;
  recurring_mistake_rate?: RecurringMistakeRate;
  totals: {
    attempts: number;
    success: number;
    success_rate: number;
    sessions: number;
    time_min: number;
  };
  modules: ModulesSummaryResponse['modules'];
  phases: PhaseSummaryResponse['phases'];
  cadence: Array<{
    id: string;
    w: number;
    d: number;
    l: number;
    rating_delta: number;
    games: number;
  }>;
  sessions: SessionRow[];
  alerts: Array<{ kind: 'warn' | 'info' | 'good'; text: string }>;
  prev?: {
    totals: ParentDashboardResponse['totals'];
    modules: ParentDashboardResponse['modules'];
    phases: ParentDashboardResponse['phases'];
  };
  deltas?: {
    attempts: number;
    success_rate: number;
    sessions: number;
    time_min: number;
  };
}
export function useParentDashboard(period: 7 | 30 | 90 = 30, compare = false) {
  const qs = new URLSearchParams({ period: String(period) });
  if (compare) qs.set('compare', 'previous');
  return useQuery<ParentDashboardResponse>({
    queryKey: ['parent', 'dashboard', period, compare],
    queryFn: () => http.get(`/api/v1/parent/dashboard?${qs.toString()}`),
  });
}
